import Button from '@mui/material/Button';
import type { DrawerProps } from '@mui/material/Drawer';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { ONE, ZERO } from '@shared/constants';
import type { User } from '@shared/types';
import clsx from 'clsx';
import type { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import MentoLogoWhite from '../Logo/MentoLogoWhite';
import SideBarAvatarMenu from '../SideBarAvatarMenu';

type SidebarProps = {
  hideSideBar?: boolean;
  isOnHomepage: boolean;
  isExpanded: boolean;
  menu: ReactNode;
  homeRoute?: string;
  onAccountSettings: () => void;
  onFeedBack: () => void;
  onSignOut: () => void;
  onToggleExpanded: () => void;
  user: User | null;
  avatarMenuMode?: 'default' | 'sign-out-only';
  isAdmin?: boolean;
  isAdminRoute?: boolean;
  lightTheme?: boolean;
} & DrawerProps;

const DrawerHeader = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  // necessary for content to be below app bar
  padding: theme.spacing(ZERO, ONE),
  ...theme.mixins.toolbar,
}));

const SideBar: FC<SidebarProps> = ({
  hideSideBar = false,
  isExpanded,
  menu,
  homeRoute = '/members',
  user,
  onAccountSettings,
  onFeedBack,
  onSignOut,
  onToggleExpanded,
  isOnHomepage = false,
  avatarMenuMode = 'default',
  isAdmin = false,
  isAdminRoute = false,
  lightTheme = false,
}) => {
  if (hideSideBar) {
    return null;
  }

  const width = isExpanded ? 226 : 72;

  return (
    <Drawer
      className="hidden sm:flex"
      PaperProps={{
        className: clsx('flex h-full flex-col', isExpanded ? 'w-[226px]' : 'w-18', {
          'border-l-4 border-sunny-500': isAdminRoute,
        }),
        sx: {
          width,
          bgcolor: lightTheme ? '#FBF5EC' : '#505A54',
          overflow: 'hidden',
          overflowY: 'hidden',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        },
      }}
      variant="permanent"
      open
      sx={{
        overflow: 'hidden',
        '& .MuiDrawer-paper': {
          overflow: 'hidden',
          overflowY: 'hidden',
          overflowX: 'hidden',
        },
      }}
    >
      <DrawerHeader>
        <div
          className={clsx(
            'flex w-full items-center',
            isExpanded ? 'justify-start px-2' : 'justify-center'
          )}
        >
          <Link aria-label="Mento" to={homeRoute}>
            {isExpanded ? (
              <MentoLogoWhite width={120} variant={lightTheme ? 'dark' : 'white'} />
            ) : lightTheme ? (
              <MentoLogoWhite iconOnly width={32} variant="dark" />
            ) : (
              <img alt="" className="cursor-pointer" height="19" src="/logo-sm.png" width="31" />
            )}
          </Link>
        </div>
      </DrawerHeader>
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
        <div
          className={clsx(
            'flex min-h-0 flex-1 flex-col justify-between pb-4',
            isExpanded ? 'items-start px-4' : 'items-center'
          )}
        >
          <div
            className={clsx(
              'min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain pb-4',
              isExpanded ? 'flex w-full flex-col gap-4' : 'flex flex-col items-center gap-4',
              {
                '[&>button]:text-white': isOnHomepage,
              }
            )}
          >
            {menu}
          </div>
          <div
            className={clsx(
              'flex w-full flex-col gap-2',
              isExpanded ? 'items-start' : 'items-center'
            )}
          >
            {isExpanded ? (
              <Button
                className={
                  lightTheme
                    ? 'justify-start text-cement-500 hover:bg-black/5'
                    : 'justify-start text-white hover:bg-white/10'
                }
                color="inherit"
                onClick={onToggleExpanded}
                startIcon={
                  <Icon className={lightTheme ? 'text-cement-500' : 'text-white'}>
                    chevron_left
                  </Icon>
                }
                sx={{ minWidth: 0, px: 1 }}
                variant="text"
              >
                Collapse sidebar
              </Button>
            ) : (
              <Tooltip placement="right" title="Expand sidebar">
                <IconButton
                  aria-label="Expand sidebar"
                  className={
                    lightTheme ? 'text-cement-500 hover:bg-black/5' : 'text-white hover:bg-white/10'
                  }
                  onClick={onToggleExpanded}
                  size="small"
                >
                  <Icon className={lightTheme ? 'text-cement-500' : 'text-white'}>
                    chevron_right
                  </Icon>
                </IconButton>
              </Tooltip>
            )}
            <SideBarAvatarMenu
              variant={isExpanded ? 'expanded' : 'icon'}
              user={user}
              onAccountSettings={onAccountSettings}
              onFeedBack={onFeedBack}
              onSignOut={onSignOut}
              menuMode={avatarMenuMode}
              isAdmin={isAdmin}
              isAdminRoute={isAdminRoute}
              lightTheme={lightTheme}
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SideBar;
export type { SidebarProps };
